import { ReactNode, isValidElement } from 'react'

import { inflect } from 'inflection'

import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import { ConditionalProperties } from 'Automations/nodeDefinitions/conditions'
import { SwitchGroup } from '../..'
import { formatConditionText } from '../formatConditionText/formatConditionText'

/**
 * Returns the label for a switch group as a ReactNode so operator
 * `formatValue` callbacks that produce rich UI (tooltips, counts, badges)
 * render correctly in the execution-summary preview. When every part is a
 * primitive the function returns a plain string so callers that compare
 * against strings keep working.
 */
export const formatGroupLabel = (
  group: SwitchGroup,
  conditionalProperties: ConditionalProperties,
  nodes: AutomationsPresentationV1Node[] = []
): ReactNode => {
  if (group.label) return group.label

  const firstCondition = group.conditions[0]
  const parts = formatConditionText(
    firstCondition,
    conditionalProperties,
    nodes
  )
  if (!parts) return 'Unknown'

  const remaining = group.conditions.length - 1
  const suffix =
    remaining > 0
      ? ` and ${remaining} more ${inflect('condition', remaining)}`
      : ''

  const hasRichSegment =
    isValidElement(parts.valueLabel) ||
    isValidElement(parts.propertyLabel) ||
    isValidElement(parts.operatorLabel)

  const operatorIsRendered =
    parts.operatorLabel !== '' && parts.operatorLabel != null
  const valueIsRendered = parts.valueLabel !== '' && parts.valueLabel != null

  if (hasRichSegment) {
    return (
      <>
        {parts.propertyLabel}
        {operatorIsRendered ? <> {parts.operatorLabel}</> : null}
        {valueIsRendered ? <> {parts.valueLabel}</> : null}
        {suffix}
      </>
    )
  }

  const segments = [
    parts.propertyLabel,
    operatorIsRendered ? parts.operatorLabel : null,
    valueIsRendered ? parts.valueLabel : null
  ].filter(segment => segment != null)

  return `${segments.join(' ')}${suffix}`
}
